import { csvLine, esc, slug, stamp } from "./exportApplications";

/**
 * Exports of the REVIEWED half of a diet's review-assignment screen.
 *
 * The assignment screen's "Assigned applications" table mixes three things an
 * admin reads differently: what is still waiting on a reviewer, what came back
 * recommended for approval, and what came back recommended for rejection. A
 * decided listing is taken out of here to be filed and signed, and the only
 * shape that is any use for that is the one the committee minutes itself in —
 * the approved names together, the rejected names together, each numbered from
 * one, each with its own count.
 *
 * So the export is SECTIONED, and the pending rows are left out of it
 * altogether: a name in a signed listing of decisions that nobody has decided
 * on is worse than no listing.
 *
 * Built from the SAME rows the table is showing — the shaped rows of
 * ProbationerReviewAssignmentController::queue — so an export can never
 * disagree with the screen it was taken from. Dependency-free in the same way
 * as ./exportApplications: CSV with a UTF-8 BOM for Excel, and a styled print
 * window where the browser's own "Save as PDF" does the writing. The shared
 * formatting (quoting, escaping, the date stamp) is imported from there rather
 * than copied, because two listings of the same sitting that quote or date
 * differently invite the reading that they came from different places.
 *
 * One thing this listing cannot show, by the shape of the screen it belongs to:
 * once the chairman finalises an application its status leaves `under_review` /
 * `with_committee`, so it drops off this screen and out of this export. What is
 * exported here is the committee's recommendations on the applications still in
 * front of it, which is exactly what the table above it says.
 */

export type ReviewedExportMeta = {
  /** The diet these decisions were taken in. */
  diet?: string;
  /** The committee that took them, when the screen knows it. */
  committee?: string;
  /**
   * The decision filter that was up when the export was taken, if any.
   *
   * Carried so a filtered listing says on its face that it is one, and so the
   * filename does too — a one-section roll that looks like the whole diet's
   * decisions is how a signed listing of "the approved" ends up filed as "all
   * of them".
   */
  filter?: string;
  /** The category filter that was up, if any — same reasoning as `filter`. */
  category?: string;
};

/** One row of the export, in the order the columns are written. */
const COLUMNS = [
  "#",
  "Applicant",
  "Membership No",
  "Email",
  "Category",
  "Chapter",
  "Reviewer",
  "Decision",
  "Decided On",
  "Days Waiting",
] as const;

/**
 * What each recorded decision actually MEANS, spelled out.
 *
 * A committee member's "approved" is a recommendation; the chairman's is the
 * institute's answer. The two are different rows in probationer_reviews and a
 * listing that prints both as the bare word "approved" reads as though the
 * decision had already been taken. See ProbationerReview::VERDICTS.
 */
const DECISION_LABEL: Record<string, string> = {
  approved: "Recommended for approval",
  accepted: "Recommended for approval",
  rejected: "Recommended for rejection",
  chairman_approved: "Approved by the chairman",
  chairman_rejected: "Rejected by the chairman",
  returned: "Returned to the applicant",
};

const decisionLabel = (decision?: string | null) =>
  !decision
    ? ""
    : DECISION_LABEL[String(decision).toLowerCase()] ??
      String(decision).replace(/_/g, " ");

export type Verdict = "approved" | "rejected" | "other";

/**
 * Which section a row belongs in, or null when nobody has reported on it.
 *
 * Matched on the WORD rather than against a fixed list, so a chairman's
 * `chairman_approved` files with the plain `approved` without this needing to
 * know every name the backend may add. `other` is the escape hatch for a
 * recorded decision that is neither — it is printed in its own block rather
 * than dropped, because a row silently missing from an export is the kind of
 * thing nobody notices until it matters.
 */
export function verdictOf(row: any): Verdict | null {
  const decision = String(row?.decision ?? "").toLowerCase();
  if (!decision) return null;
  if (decision.includes("reject")) return "rejected";
  if (decision.includes("approv") || decision.includes("accept")) return "approved";
  return "other";
}

const byName = (a: any, b: any) =>
  String(a?.name ?? "").localeCompare(String(b?.name ?? ""));

/** The reviewed rows, filed into the sections the export prints. */
export function splitReviewed(rows: any[]) {
  const approved: any[] = [];
  const rejected: any[] = [];
  const other: any[] = [];

  for (const row of rows ?? []) {
    const verdict = verdictOf(row);
    if (verdict === "approved") approved.push(row);
    else if (verdict === "rejected") rejected.push(row);
    else if (verdict === "other") other.push(row);
  }

  return {
    approved: approved.sort(byName),
    rejected: rejected.sort(byName),
    other: other.sort(byName),
    total: approved.length + rejected.length + other.length,
  };
}

/* ── Categories ──────────────────────────────────────────────────────────── */

/**
 * The three categories an entry application is made in, in the order they are
 * printed and listed.
 *
 * Spelled as user_categories spells them (UserCategorySeeder) — the row's
 * category is that table's name, and matching is case-insensitive so a renamed
 * or differently-cased row still files under its heading instead of falling
 * through to the end of the listing.
 */
export const CATEGORY_ORDER = ["Student", "Probationer", "Technician"] as const;

export const categoryOf = (row: any): string =>
  String(row?.category ?? "").trim() || "Uncategorised";

const categoryRank = (name: string) => {
  const i = CATEGORY_ORDER.findIndex((c) => c.toLowerCase() === name.toLowerCase());
  return i === -1 ? CATEGORY_ORDER.length : i;
};

const byCategory = (a: { category: string }, b: { category: string }) =>
  categoryRank(a.category) - categoryRank(b.category) ||
  a.category.localeCompare(b.category);

/**
 * Rows grouped by category, the three known ones first.
 *
 * Only the categories actually present — this is what the SCREEN uses to count
 * its chips, where a chip for a category with nothing in it is just noise.
 */
export function groupByCategory(rows: any[]): { category: string; rows: any[] }[] {
  const buckets = new Map<string, any[]>();

  for (const row of rows ?? []) {
    const key = categoryOf(row);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(row);
  }

  return Array.from(buckets, ([category, rs]) => ({ category, rows: rs.sort(byName) }))
    .sort(byCategory);
}

/**
 * The same grouping for the EXPORT, which always names all three categories.
 *
 * An empty one is printed as "none" rather than left out, because on a filed
 * listing the two are different statements: a missing Technician heading reads
 * as an oversight, while "Technician (0) — none" is the committee saying it
 * decided no technician applications this diet.
 */
function categoryBlocks(rows: any[]): { category: string; rows: any[] }[] {
  const present = groupByCategory(rows);
  const seen = new Set(present.map((g) => g.category.toLowerCase()));

  const missing = CATEGORY_ORDER.filter((c) => !seen.has(c.toLowerCase())).map((category) => ({
    category: String(category),
    rows: [] as any[],
  }));

  return [...present, ...missing].sort(byCategory);
}

/** "2026-10-14 09:30:00" → "14 Oct 2026". */
function fmtDecided(value?: string | null): string {
  if (!value) return "—";
  // Safari reads the space form as an invalid date; the T form is universal.
  const date = new Date(String(value).replace(" ", "T"));
  return isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function toRow(row: any, index: number): (string | number)[] {
  return [
    index + 1,
    row?.name || "—",
    row?.membership_no ?? "",
    row?.email ?? "",
    row?.category ?? "",
    row?.chapter ?? "",
    row?.reviewer ?? "",
    decisionLabel(row?.decision),
    fmtDecided(row?.decided_at),
    row?.waiting_days != null ? `${row.waiting_days}` : "",
  ];
}

function heading(meta: ReviewedExportMeta): string {
  return meta.diet
    ? `Reviewed Entry Applications — ${meta.diet}`
    : "Reviewed Entry Applications";
}

function filename(meta: ReviewedExportMeta, extension: string) {
  const parts = [
    "reviewed-entry-applications",
    meta.diet ? slug(meta.diet) : null,
    meta.filter ? slug(meta.filter) : null,
    meta.category ? slug(meta.category) : null,
    stamp().file,
  ].filter(Boolean);
  return `${parts.join("-")}.${extension}`;
}

/**
 * The decisions as a category × verdict table, for the summary both exports
 * carry above the listing.
 *
 * The one thing leadership asks of a diet's review that no amount of reading
 * down a listing answers quickly: how many students, probationers and
 * technicians went through, and which way.
 */
function categoryMatrix(split: ReturnType<typeof splitReviewed>) {
  const names = Array.from(
    new Set([
      ...CATEGORY_ORDER.map(String),
      ...[...split.approved, ...split.rejected, ...split.other].map(categoryOf),
    ]),
  ).sort((a, b) => byCategory({ category: a }, { category: b }));

  const count = (rows: any[], category: string) =>
    rows.filter((r) => categoryOf(r).toLowerCase() === category.toLowerCase()).length;

  return names.map((category) => ({
    category,
    approved: count(split.approved, category),
    rejected: count(split.rejected, category),
    other: count(split.other, category),
    total:
      count(split.approved, category) +
      count(split.rejected, category) +
      count(split.other, category),
  }));
}

/** The blocks the export is laid out in, in the order they are printed. */
function sectionsOf(rows: any[]) {
  const split = splitReviewed(rows);

  return {
    split,
    blocks: [
      { title: "Approved", rows: split.approved },
      { title: "Rejected", rows: split.rejected },
      // Only when there is something in it — an empty "Other decisions"
      // heading on a signed listing just raises a question it cannot answer.
      ...(split.other.length
        ? [{ title: "Other decisions", rows: split.other }]
        : []),
    ],
  };
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

export function exportReviewedSpreadsheet(rows: any[], meta: ReviewedExportMeta) {
  const { split, blocks } = sectionsOf(rows);

  const lines: string[] = [
    csvLine([`NIQS — ${heading(meta)}`]),
    ...(meta.committee ? [csvLine([`Committee: ${meta.committee}`])] : []),
    ...(meta.filter ? [csvLine([`Filtered to: ${meta.filter}`])] : []),
    ...(meta.category ? [csvLine([`Category: ${meta.category}`])] : []),
    csvLine([`Decisions listed: ${split.total}`]),
    csvLine([`Approved: ${split.approved.length}`]),
    csvLine([`Rejected: ${split.rejected.length}`]),
    csvLine([`Taken on: ${stamp().printed}`]),
    "",
    csvLine(["Breakdown by category"]),
    csvLine(["Category", "Approved", "Rejected", ...(split.other.length ? ["Other"] : []), "Total"]),
    ...categoryMatrix(split).map((r) =>
      csvLine([
        r.category,
        r.approved,
        r.rejected,
        ...(split.other.length ? [r.other] : []),
        r.total,
      ]),
    ),
  ];

  for (const block of blocks) {
    lines.push("", csvLine([`${block.title.toUpperCase()} (${block.rows.length})`]));

    if (!block.rows.length) {
      lines.push(csvLine([`No applications were ${block.title.toLowerCase()}.`]));
      continue;
    }

    // Numbered WITHIN the category, not across the section: each sub-table is
    // the roll of that category's decisions and gets read on its own.
    for (const group of categoryBlocks(block.rows)) {
      lines.push("", csvLine([`${group.category} (${group.rows.length})`]));

      if (!group.rows.length) {
        lines.push(csvLine(["None."]));
        continue;
      }

      lines.push(
        csvLine([...COLUMNS]),
        ...group.rows.map((row, i) => csvLine(toRow(row, i))),
      );
    }
  }

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it any accented name arrives as mojibake.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(meta, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

export function exportReviewedPdf(rows: any[], meta: ReviewedExportMeta) {
  const { split, blocks } = sectionsOf(rows);

  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) {
    alert("Allow pop-ups for this site to export a PDF.");
    return;
  }

  const head = `<thead><tr>${COLUMNS.map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead>`;

  const tables = blocks
    .map((block) => {
      const heading = `
  <h2 class="sec"><span>${esc(block.title)}</span><em>${block.rows.length}</em></h2>`;

      if (!block.rows.length) {
        return `${heading}
  <p class="none">No applications were ${esc(block.title.toLowerCase())}.</p>`;
      }

      const groups = categoryBlocks(block.rows)
        .map((group) => {
          const sub = `
  <h3 class="cat"><span>${esc(group.category)}</span><em>${group.rows.length}</em></h3>`;

          if (!group.rows.length) {
            return `${sub}
  <p class="none">None.</p>`;
          }

          const body = group.rows
            .map(
              (row, i) =>
                `<tr>${toRow(row, i).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`,
            )
            .join("");

          return `${sub}
  <table>${head}<tbody>${body}</tbody></table>`;
        })
        .join("");

      return heading + groups;
    })
    .join("");

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(filename(meta, "pdf").replace(/\.pdf$/, ""))}</title>
  <style>
    @page{size:A4 landscape;margin:12mm}
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{color:#111;font-size:10px}
    .head{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:14px}
    .org{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#888}
    h1{font-size:17px;font-weight:900;margin-top:4px}
    .sub{font-size:10px;color:#666;margin-top:5px;line-height:1.6}
    .sub b{color:#111}
    .stats{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:14px}
    .stat{border:1px solid #e6e6e6;border-radius:7px;padding:6px 10px;min-width:78px}
    .stat .n{display:block;font-size:15px;font-weight:900}
    .stat .l{display:block;font-size:8px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#999;margin-top:1px}
    h2.sec{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:900;letter-spacing:.12em;text-transform:uppercase;margin:18px 0 6px;padding-bottom:4px;border-bottom:1.5px solid #111;page-break-after:avoid}
    h2.sec em{font-style:normal;font-size:9px;font-weight:900;color:#fff;background:#111;border-radius:999px;padding:2px 8px}
    h3.cat{display:flex;align-items:center;gap:7px;font-size:9px;font-weight:900;letter-spacing:.14em;text-transform:uppercase;color:#555;margin:12px 0 4px;page-break-after:avoid}
    h3.cat em{font-style:normal;font-size:8px;font-weight:900;color:#555;background:#eee;border-radius:999px;padding:2px 7px}
    p.none{font-size:9px;color:#aaa;font-weight:700;margin:0 0 6px}
    table{width:100%;border-collapse:collapse;margin-bottom:6px}
    th{background:#111;color:#fff;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-align:left;padding:7px 6px}
    td{padding:6px;border-bottom:1px solid #eee;vertical-align:top}
    table.matrix{width:auto;min-width:260px;margin-bottom:16px}
    table.matrix th{background:#f3f3f3;color:#555;border-bottom:1px solid #ddd}
    table.matrix th:not(:first-child),table.matrix td:not(:first-child){text-align:right}
    table.matrix td{font-weight:700}
    table.matrix td.tot{font-weight:900}
    tr{page-break-inside:avoid}
    thead{display:table-header-group}
    .sign{display:flex;gap:40px;margin-top:26px;page-break-inside:avoid}
    .sign div{flex:1;border-top:1px solid #111;padding-top:5px;font-size:8px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#777}
    .foot{margin-top:14px;padding-top:8px;border-top:1px solid #eee;font-size:8px;color:#aaa;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="head">
    <div class="org">Nigerian Institute of Quantity Surveyors</div>
    <h1>${esc(heading(meta))}</h1>
    <div class="sub">
      ${meta.committee ? `Committee: <b>${esc(meta.committee)}</b> &middot; ` : ""}${
        split.total
      } decision(s) listed${
        meta.filter ? ` &middot; filtered to <b>${esc(meta.filter)}</b>` : ""
      }${meta.category ? ` &middot; category <b>${esc(meta.category)}</b>` : ""}<br>
      Taken on ${esc(stamp().printed)}
    </div>
  </div>

  <div class="stats">
    <div class="stat"><span class="n">${split.approved.length}</span><span class="l">Approved</span></div>
    <div class="stat"><span class="n">${split.rejected.length}</span><span class="l">Rejected</span></div>
    ${
      split.other.length
        ? `<div class="stat"><span class="n">${split.other.length}</span><span class="l">Other</span></div>`
        : ""
    }
    <div class="stat"><span class="n">${split.total}</span><span class="l">Reviewed</span></div>
  </div>

  <table class="matrix">
    <thead><tr>
      <th>Category</th><th>Approved</th><th>Rejected</th>${
        split.other.length ? "<th>Other</th>" : ""
      }<th>Total</th>
    </tr></thead>
    <tbody>${categoryMatrix(split)
      .map(
        (r) =>
          `<tr><td>${esc(r.category)}</td><td>${r.approved}</td><td>${r.rejected}</td>${
            split.other.length ? `<td>${r.other}</td>` : ""
          }<td class="tot">${r.total}</td></tr>`,
      )
      .join("")}</tbody>
  </table>
${tables}

  <div class="sign">
    <div>Secretariat</div>
    <div>Committee Chairman</div>
  </div>

  <div class="foot">NIQS Portal &middot; Entry Application Review Report</div>
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      window.onload = go;
      setTimeout(go, 6000);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
